import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CURRENCY_MAP, type CurrencyCode } from '@goyone/shared';
import { api } from '../lib/api';
import { date as fmtDate } from '../lib/format';
import { rateText, useRates, type RatesResponse } from '../lib/currency';
import { Badge, Modal } from './ui';

/**
 * أسعار الصرف اليوم مع تعديلها يدويًا.
 *
 * السعر الآلي يُجلب مرة يوميًا من خدمة أسعار عامة، وإن تعذّر يبقى آخر سعر
 * معروف. التعديل اليدوي يحفظ سعر اليوم للشركة ويتقدّم على الآلي، ولا يغيّر
 * شيئًا رُحِّل من قبل — كل حركة محفوظة بسعر يومها.
 */
export function ExchangeRatesModal({
  canEdit,
  onClose,
}: {
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const rates = useRates();
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!rates.data) return;
    setDraft(
      Object.fromEntries(rates.data.items.map((i) => [i.currency, i.rate ? String(i.rate) : ''])),
    );
  }, [rates.data]);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
    void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] });
  };

  const save = useMutation({
    mutationFn: (currency: string) =>
      api('/finance/exchange-rates', {
        method: 'PUT',
        body: { currency, rate: Number(draft[currency]) },
      }),
    onSuccess: done,
  });

  const refresh = useMutation({
    mutationFn: () => api<RatesResponse>('/finance/exchange-rates/refresh', { method: 'POST' }),
    onSuccess: done,
  });

  const error = save.error ?? refresh.error;

  return (
    <Modal
      title="أسعار الصرف"
      onClose={onClose}
      footer={
        <>
          {canEdit && (
            <button
              className="btn me-auto"
              type="button"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? 'جارٍ التحديث' : 'تحديث من السوق'}
            </button>
          )}
          <button className="btn btn-primary" type="button" onClick={onClose}>
            إغلاق
          </button>
        </>
      }
    >
      <p className="small mute mb-4">
        كم جنيهًا تساوي الوحدة من كل عملة. يُجلب السعر تلقائيًا كل يوم، ويمكنك تعديله ليطابق سعر
        بنكك — السعر اليدوي يسري على الحركات الجديدة وعلى تقييم الأرصدة في القوائم، ولا يغيّر
        حركات سُجّلت قبله.
      </p>

      {error && (
        <div className="alert alert-danger mb-4">
          {error instanceof Error ? error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {rates.isPending && <div className="skeleton" style={{ height: 180 }} />}

      {rates.data && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>العملة</th>
                <th style={{ width: 150 }}>السعر بالجنيه</th>
                <th>آخر تحديث</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rates.data.items.map((i) => {
                const def = CURRENCY_MAP[i.currency as CurrencyCode];
                const changed = (draft[i.currency] ?? '') !== (i.rate ? String(i.rate) : '');
                return (
                  <tr key={i.currency}>
                    <td className="cell-strong">
                      {def?.label ?? i.currency} <span className="tiny mute">({i.currency} {def?.symbol})</span>
                    </td>
                    <td>
                      {canEdit ? (
                        <input
                          className="input num"
                          type="number"
                          min="0"
                          step="any"
                          value={draft[i.currency] ?? ''}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, [i.currency]: e.target.value }))
                          }
                          aria-label={`سعر ${def?.label ?? i.currency}`}
                        />
                      ) : (
                        <span className="num">{rateText(i.rate)}</span>
                      )}
                    </td>
                    <td className="cell-mute">
                      {i.date ? fmtDate(i.date) : '—'}{' '}
                      {i.source && (
                        <Badge kind={i.source === 'MANUAL' ? 'info' : 'neutral'}>
                          {i.source === 'MANUAL' ? 'يدوي' : 'آلي'}
                        </Badge>
                      )}
                      {i.date && i.date < rates.data.date && (
                        <div className="tiny mute">آخر سعر معروف — تعذّر جلب سعر اليوم</div>
                      )}
                    </td>
                    {canEdit && (
                      <td className="end">
                        <button
                          className="btn btn-sm"
                          type="button"
                          disabled={!changed || !(Number(draft[i.currency]) > 0) || save.isPending}
                          onClick={() => save.mutate(i.currency)}
                        >
                          حفظ
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
